import React, { useContext, useEffect, useState } from "react";
import {
  Box, Button, CircularProgress, Stack, Stepper, Step, StepLabel,
  Typography, useTheme,
} from "@mui/material";
import OtpInput from "react-otp-input";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { gql, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { LoginWrapper } from "../Wrapper";
import FlashMessage from "../../components/FlashMessage";
import UserContext from "../../context/User";
import { requestOtp, verifyOtp, changePhoneNumber } from "../../apollo/server";
import { isCompletePhoneNumber } from "../../utils/phoneValidation";
import Logo from "../../assets/images/logo.png";

const REQUEST_OTP = gql`${requestOtp}`;
const VERIFY_OTP = gql`${verifyOtp}`;
const CHANGE_PHONE = gql`${changePhoneNumber}`;

const STEPS = ["Verify current", "Enter new number", "Verify new"];

function ChangePhone() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { profile } = useContext(UserContext);

  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [currentCode, setCurrentCode] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newCode, setNewCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const [sendOtp] = useMutation(REQUEST_OTP);
  const [verify] = useMutation(VERIFY_OTP);
  const [save] = useMutation(CHANGE_PHONE);

  // Step 0: send OTP to current phone on mount.
  // Wait until the profile has actually loaded before deciding the entry step.
  // `profile` is undefined for a moment after mount/refresh; acting on that race
  // would let the "verify current phone" gate be skipped by simply refreshing.
  useEffect(() => {
    if (profile === undefined || profile === null) return;
    if (initialized) return;
    setInitialized(true);

    if (!profile.phone) {
      // No current phone on file — skip straight to entering a new one
      setStep(1);
      return;
    }
    sendOtp({ variables: { identifier: profile.phone, channel: "SMS", purpose: "CHANGE_PHONE_CURRENT" } })
      .catch(e => setError(e.message || "Could not send code"));
  }, [profile, initialized, sendOtp]);

  const verifyCurrent = async () => {
    if (currentCode.length !== 6) return;
    setBusy(true); setError("");
    try {
      await verify({ variables: { identifier: profile.phone, channel: "SMS", purpose: "CHANGE_PHONE_CURRENT", code: currentCode } });
      setStep(1);
    } catch (e) { setError(e.message || "Incorrect code"); }
    finally { setBusy(false); }
  };

  const sendNew = async () => {
    if (!isCompletePhoneNumber(newPhone)) { setError("Enter a phone number"); return; }
    const normalized = `+${newPhone}`;
    if (normalized === profile?.phone) { setError("New number must be different from current"); return; }
    setBusy(true); setError("");
    try {
      await sendOtp({ variables: { identifier: normalized, channel: "SMS", purpose: "CHANGE_PHONE_NEW" } });
      setStep(2);
    } catch (e) { setError(e.message || "Could not send code"); }
    finally { setBusy(false); }
  };

  const finish = async () => {
    if (newCode.length !== 6) return;
    setBusy(true); setError("");
    try {
      await verify({ variables: { identifier: `+${newPhone}`, channel: "SMS", purpose: "CHANGE_PHONE_NEW", code: newCode } });
      await save({ variables: { newPhone: `+${newPhone}` } });
      navigate("/profile", { replace: true });
    } catch (e) { setError(e.message || "Could not save phone number"); }
    finally { setBusy(false); }
  };

  return (
    <LoginWrapper>
      <FlashMessage open={Boolean(error)} severity="error" alertMessage={error} handleClose={() => setError("")} />

      <Box display="flex">
        <Box m="auto" sx={{ width: 90, height: 64, overflow: "hidden" }}>
          <img alt="RushMyFood" src={Logo} style={{ width: "100%", objectFit: "contain", objectPosition: "top", transform: "scale(1.55)", transformOrigin: "top center" }} />
        </Box>
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 700, textAlign: "center", mt: 2 }}>Change Phone Number</Typography>

      <Stepper activeStep={step} alternativeLabel sx={{ mt: 3, mb: 3 }}>
        {STEPS.map(s => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
      </Stepper>

      {!initialized && (
        <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
          <CircularProgress size={28} sx={{ color: "#FF3008" }} />
        </Box>
      )}

      {initialized && step === 0 && (
        <Stack gap={2} alignItems="center">
          <Typography variant="body2" color="text.secondary" textAlign="center">
            We sent a 6-digit code to <b>{profile?.phone}</b>. Enter it to confirm you own this account.
          </Typography>
          <OtpInput
            value={currentCode}
            onChange={setCurrentCode}
            numInputs={6}
            renderSeparator={<span style={{ width: 6 }} />}
            renderInput={(p) => <input {...p} style={{ width: 40, height: 48, border: "1px solid #e5e7eb", borderRadius: 8, textAlign: "center", fontSize: 18 }} />}
          />
          <Stack direction="row" gap={1} sx={{ width: "100%" }}>
            <Button fullWidth variant="text" sx={{ textTransform: "none" }}
              onClick={() => sendOtp({ variables: { identifier: profile.phone, channel: "SMS", purpose: "CHANGE_PHONE_CURRENT" } })}>
              Resend code
            </Button>
            <Button fullWidth variant="contained" disabled={busy || currentCode.length !== 6} onClick={verifyCurrent}
              sx={{ backgroundColor: "#FF3008", "&:hover": { backgroundColor: "#e02b00" } }}>
              {busy ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Verify"}
            </Button>
          </Stack>
        </Stack>
      )}

      {initialized && step === 1 && (
        <Stack gap={2}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Enter the new phone number you want to use.
          </Typography>
          <PhoneInput
            country={"us"}
            // Lock the prefix so the customer can only edit the number part.
            countryCodeEditable={false}
            disableCountryGuess
            enableSearch
            value={newPhone}
            onChange={(p) => setNewPhone(p)}
            inputStyle={{ width: "100%", height: 48, borderRadius: 8 }}
          />
          <Button fullWidth variant="contained" disabled={busy || !newPhone} onClick={sendNew}
            sx={{ backgroundColor: "#FF3008", "&:hover": { backgroundColor: "#e02b00" } }}>
            {busy ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Send code"}
          </Button>
        </Stack>
      )}

      {initialized && step === 2 && (
        <Stack gap={2} alignItems="center">
          <Typography variant="body2" color="text.secondary" textAlign="center">
            We sent a 6-digit code to <b>+{newPhone}</b>. Enter it to confirm.
          </Typography>
          <OtpInput
            value={newCode}
            onChange={setNewCode}
            numInputs={6}
            renderSeparator={<span style={{ width: 6 }} />}
            renderInput={(p) => <input {...p} style={{ width: 40, height: 48, border: "1px solid #e5e7eb", borderRadius: 8, textAlign: "center", fontSize: 18 }} />}
          />
          <Stack direction="row" gap={1} sx={{ width: "100%" }}>
            <Button fullWidth variant="text" sx={{ textTransform: "none" }}
              onClick={() => sendOtp({ variables: { identifier: `+${newPhone}`, channel: "SMS", purpose: "CHANGE_PHONE_NEW" } })}>
              Resend code
            </Button>
            <Button fullWidth variant="contained" disabled={busy || newCode.length !== 6} onClick={finish}
              sx={{ backgroundColor: "#FF3008", "&:hover": { backgroundColor: "#e02b00" } }}>
              {busy ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Save"}
            </Button>
          </Stack>
        </Stack>
      )}
    </LoginWrapper>
  );
}

export default ChangePhone;
